/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-plusplus */
import * as vp from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from '../BrandingSettings.module.scss';
import { BrandingValues } from '../BrandingForm/BrandingForm';
import { usePendingData } from '../../../../../providers/PendingData.provider';
import { useLockedFeatureDialogue } from '../../../../../providers/LockedFeatureDialogue.provider';
import { useTranslation } from 'react-i18next';

const { Box, SubTitle, Preview, Button, Icon, UserNavPromptDialogue } = vp;
export type BrandingOption = {
  enabled: boolean;
  productType: vp.BrandingProductType;
  response?: vp.AccountBrandingResponse;
};
export type BrandingPreviewProps = {
  brandingValues: BrandingValues;
  brandingState: vp.BrandingProductType;
  brandingOptions: BrandingOption[];
  onChangeBranding: (value: vp.BrandingProductType) => void;
};
type FC = FunctionComponent<BrandingPreviewProps>;
const BrandingPreview: FC = (props: BrandingPreviewProps) => {
  const { brandingState, brandingValues, onChangeBranding, brandingOptions } = props;
  const { hasPendingData } = usePendingData();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const [movement, setMovement] = useState<vp.BrandingProductType>(vp.BrandingProductType.Email);
  const [showPrompt, setShowPrompt] = useState(false);
  const [currentIndexSlide, setCurrentIndexSlide] = useState(0);
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.BrandingSettings.Preview' });
  // Arrow function to get the next enabled product type
  const getNextEnabledProductType = (): vp.BrandingProductType => {
    const currentIndex = brandingOptions.findIndex((option) => option.productType === brandingState);
    if (currentIndex === -1) {
      throw new Error('Current product type not found in branding options');
    }

    for (let i = currentIndex + 1; i < brandingOptions.length; i++) {
      if (brandingOptions[i].enabled) {
        return brandingOptions[i].productType;
      }
    }
    for (let i = 0; i < brandingOptions.length; i++) {
      if (brandingOptions[i].enabled) {
        return brandingOptions[i].productType;
      }
    }
    // If no enabled product type found after the current one
    return brandingState;
  };

  // Arrow function to get the previous enabled product type
  const getPreviousEnabledProductType = (): vp.BrandingProductType => {
    const currentIndex = brandingOptions.findIndex((option) => option.productType === brandingState);
    if (currentIndex === -1) {
      throw new Error('Current product type not found in branding options');
    }

    for (let i = currentIndex - 1; i >= 0; i--) {
      if (brandingOptions[i].enabled) {
        return brandingOptions[i].productType;
      }
    }
    for (let i = brandingOptions.length - 1; i >= 0; i--) {
      if (brandingOptions[i].enabled) {
        return brandingOptions[i].productType;
      }
    }
    // If no enabled product type found before the current one
    return brandingState;
  };
  return (
    <div>
      <Box flx="col" gapSize="24px">
        <Box flx="row" jc="center" ai="center">
          <Box jc="fe" ai="center" pe="md">
            <Button
              id="right-button"
              label=""
              variant="outerShadow"
              background="#FBFBFB"
              color="secondary"
              noShadow={false}
              width="30px"
              height="30px"
              icon="arrowLeft"
              iconLocation="center"
              iconWidth="14px"
              iconHeight="14px"
              iconSize="sm"
              onClick={() => {
                if (hasPendingData) {
                  setMovement(getPreviousEnabledProductType());
                  setShowPrompt(true);
                } else {
                  onChangeBranding(getPreviousEnabledProductType());
                }
              }}
            />
          </Box>
          <Box jc="center" ai="center">
            <div className={styles.previewArea}>
              <Preview
                preview={brandingState}
                logo={brandingValues.Logo}
                primaryColor={brandingValues.PrimaryColor}
                secondaryColor={brandingValues.SecondaryColor}
                width={720}
                height={500}
                onChangeIndex={brandingState === 'elinx' ? setCurrentIndexSlide : undefined}
                slideShow
              />
            </div>
          </Box>
          <Box jc="fs" ai="center" ps="md">
            <Button
              id="right-button"
              label=""
              variant="outerShadow"
              background="#FBFBFB"
              color="secondary"
              noShadow={false}
              width="30px"
              height="30px"
              icon="arrowRight"
              iconLocation="center"
              iconWidth="14px"
              iconHeight="14px"
              iconSize="sm"
              onClick={() => {
                if (hasPendingData) {
                  setMovement(getNextEnabledProductType());
                  setShowPrompt(true);
                } else {
                  onChangeBranding(getNextEnabledProductType());
                }
              }}
            />
          </Box>
        </Box>
        <SubTitle center subTitle={getTitle(brandingState, currentIndexSlide, t)} />
        <Box jc="center" gapSize="24px">
          {brandingOptions.map((option) => (
            // eslint-disable-next-line jsx-a11y/interactive-supports-focus
            <div
              key={`carosel-${option.productType}`}
              role="button"
              onClick={() => {
                if (option.enabled) {
                  if (hasPendingData) {
                    setMovement(option.productType);
                    setShowPrompt(true);
                  } else {
                    onChangeBranding(option.productType);
                  }
                } else {
                  updateIsLocked(true, `${getTitle(option.productType, 0, t).toLowerCase()} branding`);
                }
              }}
              className={[
                styles.previewThumbnail,
                option.productType === brandingState ? styles.brandingActive : '',
                option.enabled ? '' : styles.brandingDisabled,
              ]
                .filter((className) => !!className)
                .join(' ')}
            >
              {option.productType === brandingState && <div className={styles.previewOverview} />}
              {!option.enabled && <div className={styles.previewDisabled} />}
              {!option.enabled && (
                <div className={styles.previewDisabledLock}>
                  <Icon icon="lock2" height="17.5px" width="15px" color="primary-light" />
                </div>
              )}
              <Preview
                height={90}
                width={120}
                preview={option.productType}
                primaryColor={option.response?.BrandingDetails?.PrimaryColor || ''}
                secondaryColor={option.response?.BrandingDetails?.SecondaryColor || ''}
                logo={option.response?.Logo || ''}
                slideShow={false}
              />
            </div>
          ))}
        </Box>
      </Box>
      <UserNavPromptDialogue
        message={t('unsavedPromptMessage')}
        onConfirm={() => {
          onChangeBranding(movement);
          setShowPrompt(false);
        }}
        onCancel={() => {
          setShowPrompt(false);
        }}
        visible={showPrompt}
      />
    </div>
  );
};
function getTitle(type: vp.BrandingProductType, index: number, t: (key: string) => string): string {
  if (type === 'elinx' && index === 1) {
    return 'Bank Verify';
  }
  switch (type) {
    case 'email':
      return t('emailTitle');
    case 'elinx':
      return t('eLinxTitle');
    case 'portal':
      return t('portalTitle');
    case 'onboarding':
      return t('onboardingTitle');
    default:
      throw new Error('Invalid title type');
  }
}

export default BrandingPreview;
